import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { MouseEvent, SyntheticEvent } from "react";
import { Button } from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { cursorsMessages } from "@/i18n/messages/cursors";
import type { Placement } from "@/lib/edge-aware-placement";
import type { Rect } from "@/lib/edge-aware-placement";
import { measureUntransformed } from "@/lib/measure-untransformed";
import {
  AgentStatusSurface,
  type CursorDisplayStatus,
} from "./agent-status-surface";
import {
  BUBBLE_INSET,
  computeEdgeAwareBubblePlacement,
} from "./use-edge-aware-bubble-placement";
import { usePointerDragOffset } from "./use-pointer-drag-offset";

interface CursorStatusBubbleProps {
  agentName: string;
  status: CursorDisplayStatus;
  expanded: boolean;
  hasMore: boolean;
  onToggleExpand: () => void;
  onDismiss?: () => void;
  draggable?: boolean;
  testId: string;
  exclusions?: Rect[];
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(max, min));
}

/**
 * Runtime status caption. It auto-flips to stay inside the canvas
 * viewport, and expanded long replies become scrollable instead of
 * growing past the visible display.
 */
export function CursorStatusBubble({
  agentName,
  status,
  expanded,
  hasMore,
  onToggleExpand,
  onDismiss,
  draggable = false,
  testId,
  exclusions,
}: CursorStatusBubbleProps) {
  const t = useT(cursorsMessages);
  const ref = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);
  const placementRef = useRef<Placement>("bottom-right");
  const translateRef = useRef({ x: Number.NaN, y: Number.NaN });
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const lastDragStatusKindRef = useRef(status.kind);
  const { handlers: dragHandlers, releaseDrag } = usePointerDragOffset(
    draggable,
    (dx, dy) => {
      dragOffsetRef.current = {
        x: dragOffsetRef.current.x + dx,
        y: dragOffsetRef.current.y + dy,
      };
      applyPlacementRef.current();
    },
  );
  const applyPlacementRef = useRef<() => void>(() => {});
  const rafRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const applyPlacement = () => {
      const box = measureUntransformed(el);
      const pane =
        (el.closest("[data-canvas-viewport]") as HTMLElement | null) ??
        document.documentElement;
      const v = pane.getBoundingClientRect();
      // Retained marks own stable captions. The live reply yields to their
      // measured bounds, including after its point target has been released.
      const callouts = Array.from(
        document.querySelectorAll<HTMLElement>(
          '[data-agent-annotation-callout="true"]',
        ),
        (callout) => callout.getBoundingClientRect(),
      );
      const result = computeEdgeAwareBubblePlacement({
        anchor: { x: box.left, y: box.top },
        size: { width: box.width, height: box.height },
        viewport: {
          left: v.left,
          top: v.top,
          right: v.right,
          bottom: v.bottom,
        },
        exclusions: [...(exclusions ?? []), ...callouts],
        reply: status.kind === "reply",
      });
      const baseLeft = result.left;
      const baseTop = result.top;
      const offset = dragOffsetRef.current;
      const left = clamp(
        baseLeft + offset.x,
        v.left + BUBBLE_INSET,
        v.right - BUBBLE_INSET - box.width,
      );
      const top = clamp(
        baseTop + offset.y,
        v.top + BUBBLE_INSET,
        v.bottom - BUBBLE_INSET - box.height,
      );
      dragOffsetRef.current = {
        x: left - baseLeft,
        y: top - baseTop,
      };
      const nextTranslate = {
        x: left - box.left,
        y: top - box.top,
      };
      const prev = translateRef.current;
      if (
        !Number.isFinite(prev.x) ||
        !Number.isFinite(prev.y) ||
        Math.abs(prev.x - nextTranslate.x) > 0.5 ||
        Math.abs(prev.y - nextTranslate.y) > 0.5
      ) {
        translateRef.current = nextTranslate;
        el.style.transform = `translate(${nextTranslate.x}px, ${nextTranslate.y}px)`;
      }
      if (placementRef.current !== result.placement) {
        placementRef.current = result.placement;
        el.dataset.placement = result.placement;
      }
    };
    applyPlacementRef.current = applyPlacement;

    const tick = () => {
      applyPlacement();
      rafRef.current = requestAnimationFrame(tick);
    };
    const updateMaxHeight = () => {
      const pane =
        (el.closest("[data-canvas-viewport]") as HTMLElement | null) ??
        document.documentElement;
      const v = pane.getBoundingClientRect();
      setMaxHeight(Math.max(80, v.bottom - v.top - BUBBLE_INSET * 2));
    };
    updateMaxHeight();
    applyPlacement();
    rafRef.current = requestAnimationFrame(tick);
    const ro = new ResizeObserver(() => {
      updateMaxHeight();
      applyPlacement();
    });
    ro.observe(el);
    window.addEventListener("resize", updateMaxHeight);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      applyPlacementRef.current = () => {};
      ro.disconnect();
      window.removeEventListener("resize", updateMaxHeight);
    };
  }, [status, expanded, exclusions]);

  useEffect(() => {
    const statusKindChanged = lastDragStatusKindRef.current !== status.kind;
    lastDragStatusKindRef.current = status.kind;
    if (draggable && !statusKindChanged) return;
    releaseDrag(ref.current);
    dragOffsetRef.current = { x: 0, y: 0 };
    translateRef.current = { x: Number.NaN, y: Number.NaN };
    applyPlacementRef.current();
  }, [draggable, releaseDrag, status.kind]);

  const stopCursorEvent = useCallback((event: SyntheticEvent) => {
    event.stopPropagation();
  }, []);
  const handleDismiss = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onDismiss?.();
    },
    [onDismiss],
  );
  const overlayControlCount = Number(!!onDismiss);
  const actionControls = overlayControlCount ? (
    <div
      className="absolute right-1 top-1 z-10 flex gap-0.5"
      data-testid={`${testId}-overlay-controls`}
    >
      {onDismiss && (
        <Button
          type="button"
          variant="secondary"
          size="iconSm"
          onPointerDown={stopCursorEvent}
          onClick={handleDismiss}
          className={cn(
            "!h-4 !w-4 rounded-full",
            "text-[var(--color-readable-overlay-secondary)] shadow-glass-sm",
            "opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100",
            "hover:text-primary",
          )}
          aria-label={t("dismissCursor")}
          data-testid={`${testId}-dismiss`}
        >
          <Icons.X className="h-3 w-3" />
        </Button>
      )}
    </div>
  ) : null;

  return (
    <div
      ref={ref}
      className={cn(
        "pointer-events-auto relative w-max max-w-[364px] shrink-0 will-change-transform",
        overlayControlCount > 0 && "group",
        draggable &&
          "touch-none cursor-grab data-[dragging=true]:cursor-grabbing",
      )}
      data-interactive={draggable || onDismiss || hasMore ? "true" : undefined}
      data-draggable={draggable ? "true" : undefined}
      data-placement={placementRef.current}
      data-cursor-reply={status.kind === "reply" ? status.text : undefined}
      data-testid={`${testId}-bubble-positioner`}
      {...dragHandlers}
      onClick={stopCursorEvent}
      onWheel={stopCursorEvent}
    >
      <AgentStatusSurface
        agentName={agentName}
        status={status}
        expanded={expanded}
        hasMore={hasMore}
        maxHeight={maxHeight}
        onToggleExpand={onToggleExpand}
        onPointerDownControl={stopCursorEvent}
        overlayControls={actionControls}
        overlayControlCount={overlayControlCount}
        testId={testId}
      />
    </div>
  );
}
